import React, { useEffect, useState } from "react";
import axios from "axios";
import { Save, Trash2, Plus, RefreshCw, Globe, Phone, MapPin, Facebook, Twitter, Menu, LogIn, UserPlus, LayoutDashboard, LogOut } from "lucide-react";

const ManageNavbar = () => {
  const [formData, setFormData] = useState({
    logo: "",
    contact: { phone: "", address: "" },
    socials: [],
    menuItems: [],
    authLabels: { login: "", signup: "", dashboard: "", logout: "" },
    isActive: true,
  });
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [message, setMessage] = useState({ text: "", type: "" });

  // Fetch Data
  const fetchData = async () => {
    try {
      setLoading(true);
      const res = await axios.get("https://all-in-one-youtube-cc51e2f64b53.herokuapp.com/api/navbar-v5");
      setFormData(res.data.data);
    } catch (error) {
      setMessage({ text: "Error loading data", type: "danger" });
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchData();
  }, []);

  // Handle Input Change
  const handleChange = (e, section, index, field) => {
    const { name, value, checked, type } = e.target;
    setFormData((prev) => {
      const newData = { ...prev };
      if (section) {
        if (index !== undefined) {
          // Array update (socials/menuItems)
          newData[section][index][field] = value;
        } else {
          // Nested object update (contact/authLabels)
          newData[section][name] = value;
        }
      } else {
        // Root level update
        newData[name] = type === "checkbox" ? checked : value;
      }
      return newData;
    });
  };

  // Add/Remove Array Items
  const addItem = (section) => {
    const newItem = section === "socials" 
      ? { name: "New Social", url: "#", iconClass: "icofont-facebook", styleClass: "fb" }
      : { label: "New Link", link: "/" };
    setFormData((prev) => ({ ...prev, [section]: [...prev[section], newItem] }));
  };

  const removeItem = (section, index) => {
    setFormData((prev) => ({
      ...prev,
      [section]: prev[section].filter((_, i) => i !== index),
    }));
  };

  // Submit Data
  const handleSubmit = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      await axios.put("https://all-in-one-youtube-cc51e2f64b53.herokuapp.com/api/navbar-v5", formData);
      setMessage({ text: "Navbar updated successfully!", type: "success" });
    } catch (error) {
      setMessage({ text: "Failed to update navbar.", type: "danger" });
    } finally {
      setSaving(false);
    }
  };

  // Reset Data
  const handleReset = async () => {
    if (window.confirm("Are you sure you want to reset to default settings?")) {
      try {
        await axios.post("https://all-in-one-youtube-cc51e2f64b53.herokuapp.com/api/navbar-v5/reset");
        fetchData();
        setMessage({ text: "Reset to defaults.", type: "info" });
      } catch (error) {
        setMessage({ text: "Reset failed.", type: "danger" });
      }
    }
  };

  if (loading) return <div className="text-center mt-5">Loading Admin Panel...</div>;

  return (
    <div className="container py-4">
      <div className="d-flex justify-content-between align-items-center mb-4">
        <h2 className="fw-bold"><LayoutDashboard className="me-2" size={28} /> Manage Navbar</h2>
        <div>
          <button className="btn btn-outline-secondary me-2" onClick={handleReset}>
            <RefreshCw size={18} className="me-1" /> Reset Defaults
          </button>
        </div>
      </div>

      {message.text && (
        <div className={`alert alert-${message.type} alert-dismissible fade show`} role="alert">
          {message.text}
          <button type="button" className="btn-close" onClick={() => setMessage({ text: "", type: "" })}></button>
        </div>
      )}

      <form onSubmit={handleSubmit}>
        <div className="row">
          
          {/* General Settings */}
          <div className="col-md-12 mb-4">
            <div className="card shadow-sm">
              <div className="card-header text-dark fw-bold"><Globe size={20} className="me-2" /> General Settings</div>
              <div className="card-body">
                <div className="mb-3">
                  <label className="form-label text-dark text-dark">Logo URL</label>
                  <input
                    type="text" className="form-control" name="logo"
                    value={formData.logo} onChange={(e) => handleChange(e)}
                    placeholder="/assets/images/logo/logo.png"
                  />
                </div>
                <div className="form-check form-switch">
                  <input
                    className="form-check-input" type="checkbox" name="isActive"
                    checked={formData.isActive} onChange={(e) => handleChange(e)}
                  />
                  <label className="form-check-label">Active Section</label>
                </div>
              </div>
            </div>
          </div>

          {/* Contact Info */}
          <div className="col-md-6 mb-4">
            <div className="card shadow-sm h-100">
              <div className="card-header text-dark fw-bold"><Phone size={20} className="me-2" /> Contact Info</div>
              <div className="card-body">
                <div className="mb-3">
                  <label className="form-label text-dark text-dark">Phone Number</label>
                  <div className="input-group">
                    <span className="input-group-text"><Phone size={16}/></span>
                    <input
                      type="text" className="form-control"
                      value={formData.contact.phone}
                      onChange={(e) => handleChange(e, "contact", null, "phone")}
                    />
                  </div>
                </div>
                <div className="mb-3">
                  <label className="form-label text-dark text-dark">Address</label>
                  <div className="input-group">
                    <span className="input-group-text"><MapPin size={16}/></span>
                    <input
                      type="text" className="form-control"
                      value={formData.contact.address}
                      onChange={(e) => handleChange(e, "contact", null, "address")}
                    />
                  </div>
                </div>
              </div>
            </div>
          </div>

          {/* Auth Labels */}
          <div className="col-md-6 mb-4">
            <div className="card shadow-sm h-100">
              <div className="card-header text-dark fw-bold"><LogIn size={20} className="me-2" /> Authentication Labels</div>
              <div className="card-body">
                <div className="row g-2">
                  <div className="col-6">
                    <label className="form-label text-dark small text-dark">Login Text</label>
                    <input type="text" className="form-control form-control-sm" name="login" value={formData.authLabels.login} onChange={(e) => handleChange(e, "authLabels")} />
                  </div>
                  <div className="col-6">
                    <label className="form-label text-dark small text-dark">Signup Text</label>
                    <input type="text" className="form-control form-control-sm" name="signup" value={formData.authLabels.signup} onChange={(e) => handleChange(e, "authLabels")} />
                  </div>
                  <div className="col-6">
                    <label className="form-label text-dark small text-dark">Dashboard Text</label>
                    <input type="text" className="form-control form-control-sm" name="dashboard" value={formData.authLabels.dashboard} onChange={(e) => handleChange(e, "authLabels")} />
                  </div>
                  <div className="col-6">
                    <label className="form-label text-dark small text-dark">Logout Text</label>
                    <input type="text" className="form-control form-control-sm" name="logout" value={formData.authLabels.logout} onChange={(e) => handleChange(e, "authLabels")} />
                  </div>
                </div>
              </div>
            </div>
          </div>

          {/* Menu Items */}
          <div className="col-md-6 mb-4">
            <div className="card shadow-sm h-100">
              <div className="card-header text-dark d-flex justify-content-between align-items-center fw-bold">
                <span><Menu size={20} className="me-2" /> Menu Items</span>
                <button type="button" className="btn btn-sm btn-outline-primary" onClick={() => addItem("menuItems")}>
                  <Plus size={16} />
                </button>
              </div>
              <div className="card-body" style={{ maxHeight: "300px", overflowY: "auto" }}>
                {formData.menuItems.map((item, index) => (
                  <div key={index} className="row g-2 mb-2 align-items-center">
                    <div className="col-5">
                      <input type="text" className="form-control form-control-sm" placeholder="Label" value={item.label} onChange={(e) => handleChange(e, "menuItems", index, "label")} />
                    </div>
                    <div className="col-5">
                      <input type="text" className="form-control form-control-sm" placeholder="Link (e.g. /about)" value={item.link} onChange={(e) => handleChange(e, "menuItems", index, "link")} />
                    </div>
                    <div className="col-2">
                      <button type="button" className="btn btn-sm btn-outline-danger w-100" onClick={() => removeItem("menuItems", index)}>
                        <Trash2 size={14} />
                      </button>
                    </div>
                  </div>
                ))}
                {formData.menuItems.length === 0 && <p className="text-muted small text-center">No menu items added.</p>}
              </div>
            </div>
          </div>

          {/* Social Links */}
          <div className="col-md-6 mb-4">
            <div className="card shadow-sm h-100">
              <div className="card-header text-dark d-flex justify-content-between align-items-center fw-bold">
                <span><Facebook size={20} className="me-2" /> Social Links</span>
                <button type="button" className="btn btn-sm btn-outline-primary" onClick={() => addItem("socials")}>
                  <Plus size={16} />
                </button>
              </div>
              <div className="card-body" style={{ maxHeight: "300px", overflowY: "auto" }}>
                {formData.socials.map((social, index) => (
                  <div key={index} className="card bg-light mb-2 p-2 border-0">
                    <div className="row g-2 mb-2">
                      <div className="col-6">
                        <label className="small text-muted">Icon Class (Icofont)</label>
                        <input type="text" className="form-control form-control-sm" value={social.iconClass} onChange={(e) => handleChange(e, "socials", index, "iconClass")} placeholder="icofont-facebook" />
                      </div>
                      <div className="col-6">
                        <label className="small text-muted">Style Class</label>
                        <input type="text" className="form-control form-control-sm" value={social.styleClass} onChange={(e) => handleChange(e, "socials", index, "styleClass")} placeholder="fb" />
                      </div>
                    </div>
                    <div className="input-group input-group-sm">
                      <span className="input-group-text">URL</span>
                      <input type="text" className="form-control" value={social.url} onChange={(e) => handleChange(e, "socials", index, "url")} />
                      <button className="btn btn-outline-danger" type="button" onClick={() => removeItem("socials", index)}><Trash2 size={14} /></button>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          </div>

        </div>

        <div className="d-flex justify-content-end gap-2 mt-4">
          <button type="submit" className="btn btn-success px-4" disabled={saving}>
            {saving ? "Saving..." : <><Save size={18} className="me-2" /> Save Changes</>}
          </button>
        </div>
      </form>
    </div>
  );
};

export default ManageNavbar;